/* @jsx jsx */
import React from 'react'
import { Button, jsx, Flex } from 'theme-ui'

import PropTypes from 'prop-types'
import {
  useAnalytics,
  useTranslate,
} from '@chordcommerce/gatsby-theme-autonomy'
import { GatsbyImage } from 'gatsby-plugin-image'
import StyledWrapper from '../../Generic/StyledWrapper'
import useVariantAvailability from '../../../hooks/components/use-variant-availability'
import StarRating from '../StarRating'
import ProductButton from '../ProductButton'
import BazaarStar from '../StarRating/BazaarStar'
import StickerTag from './StickerTag'
import { toURL } from '~/utils/intl/localePrefix'
import Heading from '~/components/IntlComponents/Heading'
import { productPathBuilder } from '~/utils/builder/index.cjs'
import TriggerWarningAroundDisabledButtonsWrapper from '~/components/IntlComponents/TriggerWarningAroundDisabledButtonsWrapper'
import useLocaleCartLogic from '~/utils/intl/context/hooks/use-locale-cart-logic'
import { renderImageOrVideo } from '~/utils/render-image-or-video'

export const ProductGridItem = ({
  item,
  isFeatured,
  sticker,
  onClick,
  onNoAvailability,
  locale,
}) => {
  const { hasGroupHitQuantityLimit, cart } = useLocaleCartLogic()
  const { trackProductClicked } = useAnalytics()
  const { isAvailable } = useVariantAvailability({ sku: item.sku })
  const {
    product,
    size,
    price,
    productGridImage,
    sku,
    soldOut,
    discountPercentage,
    usePreFilledBundle,
    omsTag,
  } = item
  const { name, slug, isGoods } = product[0]
  const [added, setAdded] = React.useState(false)
  const translate = useTranslate()

  const isDisabledByLocaleLimitations = hasGroupHitQuantityLimit(slug)
  const handleAddToCart = async e => {
    setAdded(true)
    e.preventDefault()
    const finalSku = usePreFilledBundle?.masterSku || sku
    await onClick(finalSku, 1)
    await fetch('/.netlify/functions/add-remove-tag-to-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order: cart.number,
        addTag: omsTag,
      }),
    })
      .then(response => response.json())
      .catch(error =>
        console.error('[Gooder] Homepage Product Tag Order Error', error),
      )
  }

  const onItemOutOfStock = item => {
    setAdded(true)
    onNoAvailability(item)
  }

  const promoDiscountPrice = discountPercentage
    ? price - (discountPercentage / 100) * price
    : null
  const calculatedPrice = promoDiscountPrice ?? price

  let label = soldOut
    ? translate('product.sold_out')
    : !isAvailable
    ? translate('product.join_waitlist')
    : (size ? `Get ${size}` : 'Buy Now') + ` | $${calculatedPrice?.toFixed(2)}`

  if (added) {
    label = isAvailable
      ? translate('product.added_to_cart_simple')
      : translate('product.short_notify_available_thank_you')
  }

  let slugToUse = productPathBuilder(slug)

  const localizedLink = toURL(`/${locale}/`, slugToUse)

  return (
    <StyledWrapper
      key={localizedLink}
      forwardSx={{ gridColumn: isFeatured ? '1 / span 2' : 'auto' }}
    >
      <Flex
        sx={{
          flexFlow: 'column nowrap',
          width: '100%',
          position: 'relative',
          alignItems: 'center',
          minWidth: ['168px', '200px'],
          justifyContent: 'space-between',
        }}
      >
        <div
          sx={{
            flex: 1,
            position: 'relative',
            textAlign: 'center',
          }}
        >
          {sticker && (
            <StickerTag
              title={sticker.title}
              stroke={sticker.stroke}
              fill={sticker.fill}
            />
          )}
          <a
            key={localizedLink}
            href={localizedLink}
            onClick={() => trackProductClicked({ product })}
          >
            <div
              sx={{
                height: '220px',
                position: 'relative',
                width: '100%',
                mx: 'auto',
                mb: '1rem',
              }}
            >
              {productGridImage &&
                renderImageOrVideo(productGridImage, {
                  VideoComponent: item => (
                    <video
                      src={item.file.url}
                      controls={false}
                      autoPlay
                      muted
                      loop
                      sx={{
                        position: 'relative',
                        zIndex: 2,
                        height: '100%',
                        maxHeight: '220px',
                        width: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ),
                  ImageComponent: item => (
                    <GatsbyImage
                      image={item.gatsbyImageData}
                      alt={item.title}
                      objectFit={'contain'}
                      style={{ maxHeight: '100%', height: '100%' }}
                      imgStyle={{
                        zIndex: 2,
                        height: '100%',
                        maxHeight: '220px',
                      }}
                    />
                  ),
                })}
            </div>
          </a>
          <a
            href={localizedLink}
            onClick={() => trackProductClicked({ product })}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 'auto',
              minHeight: '5rem',
            }}
          >
            <Heading
              as="h4"
              sx={{
                color: 'governorBay',
                fontSize: [18, 24],
                lineHeight: '24px',
                fontWeight: 900,
                textAlign: 'center',
                mb: '1rem',
                maxWidth: '10ch',
                mr: 0,
              }}
            >
              {name}
            </Heading>
          </a>
        </div>
        {!isGoods && (
          <BazaarStar
            inline
            product={product[0]}
            forwardSx={{
              width: '100%',
              justifyContent: 'center',
              mb: ['8px', '16px'],
            }}
          />
        )}
        <TriggerWarningAroundDisabledButtonsWrapper
          disabledByLocaleLimitations={isDisabledByLocaleLimitations}
        >
          <Button
            disabled={isDisabledByLocaleLimitations || soldOut}
            bg={added ? '#FFDD00' : 'governorBay'}
            color={added ? 'governorBay' : '#FFDD00'}
            onClick={
              isAvailable
                ? handleAddToCart
                : !soldOut
                ? () => onItemOutOfStock(item)
                : null
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '50px',
              paddingX: '16px',
              maxWidth: ['180px', '200px'],
              fontSize: [16, 20],
              lineHeight: ['24px', '25.77px'],
              fontWeight: 800,
              ':hover': {
                backgroundColor: 'override.altButtonBackground',
                color: 'override.altButtonColor',
              },
            }}
          >
            {label}
          </Button>
        </TriggerWarningAroundDisabledButtonsWrapper>
      </Flex>
    </StyledWrapper>
  )
}

export const FluidImgPropType = PropTypes.shape({
  aspectRatio: PropTypes.number,
  src: PropTypes.string,
  srcSet: PropTypes.string,
  sizes: PropTypes.string,
})

export const GridItemPropType = PropTypes.shape({
  price: PropTypes.number.isRequired,
  size: PropTypes.string,
  sku: PropTypes.string,
  productGridImage: PropTypes.object,
  onClick: PropTypes.func,
  product: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      images: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          gatsbyImageData: FluidImgPropType,
        }),
      ).isRequired,
    }),
  ),
})

ProductGridItem.propTypes = {
  item: GridItemPropType,
  sticker: PropTypes.shape({
    title: PropTypes.string.isRequired,
    stroke: PropTypes.string.isRequired,
    fill: PropTypes.string.isRequired,
  }),
  isFeatured: PropTypes.bool,
}
